import React, { useState, useEffect } from "react";
import TreeView from "react-treeview";
import classnames from "classnames";
import _, { get, set } from "lodash";
import Fuse from "fuse.js";
import "react-treeview/react-treeview.css";
import { GP5 } from "../../../models/gp5";
import { QualityAlert } from "../../../models/quality-alert";
import { getGP5Data as getGP5DataAction, selectGP5Data, selectGP5Loading, selectGP5Error } from "../../../reducers/qualityAlertSlice";
import { useAppDispatch, useAppSelector } from "../../../reducers/store";

interface GP5TreeProps {
  readOnly?: boolean;
  onSelectLeaf?: (qualityAlert: QualityAlert) => void;
  selectedLeaf?: number;
  filter?: string;
  filterThreshold?: number;
  className?: string;
  qualityAlert?: QualityAlert | null;
}

const GP5Tree: React.FC<GP5TreeProps> = (props) => {
  const {
    readOnly = false,
    selectedLeaf,
    filter,
    filterThreshold,
    qualityAlert,
    onSelectLeaf,
  } = props;

  const dispatch = useAppDispatch();
  const gp5Data = useAppSelector(selectGP5Data);
  const isLoading = useAppSelector(selectGP5Loading);
  const error = useAppSelector(selectGP5Error);

  const [collapsedBookkeeping, setCollapsedBookkeeping] = useState<{
    [key: number]: boolean;
  }>({});
  const [selectedLeafState, setSelectedLeafState] = useState<number | null>(
    null,
  );

  useEffect(() => {
    getGP5Data();
  }, []);

  useEffect(() => {
    const initialCollapsed = !readOnly;
    const bookkeeping: { [key: number]: boolean } = {};
    if (!gp5Data) {
      return;
    }
    gp5Data.forEach((model) => {
      bookkeeping[model.id] = initialCollapsed;
    });
    setCollapsedBookkeeping(bookkeeping);
  }, [gp5Data, readOnly]);

  const clearSelection = () => {
    setSelectedLeafState(null);
  };

  const handleSelectLeaf = (id: number) => {
    if (!qualityAlert) {
      return;
    }
    setSelectedLeafState(id);
    qualityAlert.gp5 = id;
    onSelectLeaf?.({ ...qualityAlert });
  };

  const getGP5Data = async () => {
    await dispatch(getGP5DataAction());
  };

  const handleClick = (id: number) => {
    if (!readOnly) {
      setCollapsedBookkeeping((prev) => ({
        ...prev,
        [id]: !prev[id],
      }));
    }
  };

  const renderParent = (parent: any, children: any) => {
    const bullet = readOnly ? (
      <div className="mr-2 h-2 w-2 min-w-[8px] bg-contain bg-no-repeat text-gray-600">
        &#8226;
      </div>
    ) : null;

    const label = (
      <span className="inline-flex items-center">
        {bullet}
        <span
          className={classnames(
            "cursor-pointer select-none rounded-sm font-semibold hover:bg-[#dcf5f3]",
            {
              "cursor-pointer": !readOnly,
              "text-gray-600": readOnly,
            },
          )}
          onClick={() => handleClick(parent.id)}
        >
          {parent.desc}
        </span>
      </span>
    );

    return (
      <TreeView
        nodeLabel={label}
        key={parent.id}
        collapsed={collapsedBookkeeping[parent.id]}
      >
        {children[parent.id]?.map((child: any) => {
          if (children[child.id]) {
            return renderParent(child, children);
          } else {
            const selectedClass =
              selectedLeafState === child.id ? "bg-blue-200" : "";
            return (
              <div
                key={child.id}
                className={classnames(
                  "ml-4 flex cursor-pointer select-none items-center px-2 pb-1 text-[14px] hover:bg-[#dcf5f3]",
                  selectedClass,
                  {
                    "text-gray-600": readOnly,
                  },
                )}
                onClick={() => handleSelectLeaf(child.id)}
              >
                {bullet} {child.desc}
              </div>
            );
          }
        })}
      </TreeView>
    );
  };

  const renderTree = () => {
    const children: { [key: string]: any[] } = {};
    let parents: any[] = [];
    let other: any[] = [];
    if (!gp5Data) {
      return null;
    }

    if (readOnly && selectedLeaf !== undefined) {
      let item = gp5Data.find((model) => model.id === selectedLeaf);
      while (item && item?.parent !== null) {
        children[item.parent] = children[item.parent] || [];
        children[item.parent].push(item);
        item = gp5Data[item.parent];
      }
      parents.push(item);
    } else {
      gp5Data.forEach((item) => {
        if (item.desc.toLowerCase() === "other") {
          other.push(item);
          return;
        }
        if (item.parent !== null) {
          children[item.parent] = children[item.parent] || [];
          children[item.parent].push(item);
        } else {
          parents.push(item);
        }
      });

      other.forEach((o) => {
        if (o.parent !== null) {
          children[o.parent] = children[o.parent] || [];
          children[o.parent].push(o);
        } else {
          parents.push(o);
        }
      });
    }

    if (filter) {
      const leafNodes = Object.values(children).flat();
      const fuseResults = new Fuse(leafNodes, {
        keys: ["desc"],
        threshold: filterThreshold || 0.0,
      }).search(filter);

      // TODO ADAM double check this
      const validIds = fuseResults.map((result) => {
        let item = result.item;
        const ids = [item.id];
        while (item?.parent !== null) {
          ids.push(item.parent);
          item = gp5Data[item.parent];
        }
        return ids;
      });

      parents = parents.filter((parent) => validIds.flat().includes(parent.id));
      Object.keys(children).forEach((key) => {
        children[key] = children[key].filter((child) =>
          validIds.flat().includes(child.id),
        );
      });
    }

    if (parents.length === 0) {
      return <div>No matches found.</div>;
    }

    return parents.map((parent) => renderParent(parent, children));
  };

  return (
    <div
      className={
        (classnames(
          "relative mt-2 max-h-[65vh] min-h-[70px] w-[95%] overflow-auto border border-gray-300 bg-white p-1 text-[17px]",
          props.className,
        ),
        "text-black")
      }
    >
      <div>{renderTree()}</div>
    </div>
  );
};

export default GP5Tree;
